import React, { useEffect, useRef, useState } from "react";

/**
 * Animates an integer/float from 0 to `value` once when scrolled into view.
 * Supports prefix/suffix and decimals.
 */
export function CountUp({
  value,
  duration = 1500,
  prefix = "",
  suffix = "",
  decimals = 0,
  className = "",
  separator = ",",
}) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const start = performance.now();
            const animate = (now) => {
              const p = Math.min(1, (now - start) / duration);
              // Ease out cubic
              const eased = 1 - Math.pow(1 - p, 3);
              setDisplay(value * eased);
              if (p < 1) requestAnimationFrame(animate);
              else setDisplay(value);
            };
            requestAnimationFrame(animate);
          }
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value, duration]);

  const fmt = (n) => {
    const fixed = decimals > 0 ? n.toFixed(decimals) : Math.floor(n).toString();
    const [intPart, dec] = fixed.split(".");
    const withSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    return dec ? `${withSep}.${dec}` : withSep;
  };

  return (
    <span ref={ref} className={className}>
      {prefix}
      {fmt(display)}
      {suffix}
    </span>
  );
}

/** Animated SVG underline that draws on scroll-in */
export function DrawUnderline({ color = "#B8D958", className = "" }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setInView(true)),
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <svg
      ref={ref}
      className={className}
      width="60"
      height="8"
      viewBox="0 0 60 8"
      fill="none"
    >
      <path
        d="M2 5 Q15 1, 30 4 T58 4"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
        style={{
          strokeDasharray: 70,
          strokeDashoffset: inView ? 0 : 70,
          transition: "stroke-dashoffset 1.2s 0.2s ease-out",
        }}
      />
    </svg>
  );
}
